import React from "react";
import { useLocation } from "react-router-dom";
import "./IndustriesSec1.css";

export default function IndustriesSec1({ currentLanguage = "en" }) {
  const location = useLocation();
  const isFrench = currentLanguage === "fr";

  // =================== ENGLISH DATA ===================
  const dataEN = {
    default: {
      title: "CHALLENGES & SOLUTIONS",
      subtitle: "Addressing critical industry pain points",
      top: [
        {
          icon: "/assets/icons/regulatory.png",
          title: "Regulatory Complexity",
          text: "Evolving compliance requirements across multiple jurisdictions",
        },
        {
          icon: "/assets/icons/market.png",
          title: "Market Volatility",
          text: "Unpredictable market conditions affecting investment strategies",
        },
        {
          icon: "/assets/icons/digital.png",
          title: "Digital Transformation",
          text: "Legacy systems hindering operational efficiency",
        },
      ],
      bottom: [
        {
          icon: "/assets/icons/reg-check.png",
          text: "Automated compliance monitoring and expert insights",
        },
        {
          icon: "/assets/icons/portfolio-check.png",
          text: "AI-powered risk analytics and adaptive strategies",
        },
        {
          icon: "/assets/icons/tech-check.png",
          text: "Modern technology implementation & process optimization",
        },
      ],
    },
    assetManagement: {
      title: "ASSET MANAGEMENT RISKS & SOLUTIONS",
      subtitle:
        "Addressing real asset management pressures with practical, performance-driven solutions",
      top: [
        {
          icon: "/assets/icons/regulatory.png",
          title: "Regulatory Burden",
          text: "Asset managers face constant pressure to meet evolving global regulations.",
        },
        {
          icon: "/assets/icons/market.png",
          title: "Market Volatility",
          text: "Unpredictable interest rates and geopolitical shifts make portfolio performance inconsistent.",
        },
        {
          icon: "/assets/icons/digital.png",
          title: "Fee Compression",
          text: "Growing competition and investor demand for lower fees reduce margins and limit operational flexibility.",
        },
      ],
      bottom: [
        {
          icon: "/assets/icons/reg-check.png",
          text: "Compliance Intelligence: We streamline compliance workflows and build audit-ready reporting structures that reduce manual errors.",
        },
        {
          icon: "/assets/icons/portfolio-check.png",
          text: "Stabilized Portfolios: We design resilient allocation models and apply scenario-based risk modeling to keep performance steady.",
        },
        {
          icon: "/assets/icons/tech-check.png",
          text: "Value Optimization: We improve cost structures and identify new revenue pathways that protect margins.",
        },
      ],
    },
    banking: {
      title: "BANKING PRESSURES, PRACTICAL ANSWERS",
      subtitle:
        "Solving the financial, operational, and regulatory hurdles shaping modern banking.",
      top: [
        {
          icon: "/assets/icons/regulatory.png",
          title: "Compliance Overload",
          text: "Frequent policy changes make compliance one of the most resource-heavy functions.",
        },
        {
          icon: "/assets/icons/market.png",
          title: "Slowing Growth",
          text: "Outdated systems increase operational costs and expose cybersecurity gaps.",
        },
        {
          icon: "/assets/icons/digital.png",
          title: "Declining Customer Trust",
          text: "With competition from fintechs and digital banks, traditional banks struggle to maintain customer trust.",
        },
      ],
      bottom: [
        {
          icon: "/assets/icons/reg-check.png",
          text: "Smarter Governance: Automated monitoring and centralized risk controls reduce manual work and cut compliance costs.",
        },
        {
          icon: "/assets/icons/portfolio-check.png",
          text: "Digital Modernization: Upgrading to secure, cloud-enabled platforms improves speed and strengthens resilience.",
        },
        {
          icon: "/assets/icons/tech-check.png",
          text: "Experience Reinvention: Data-driven journey redesign restores customer trust and long-term loyalty.",
        },
      ],
    },
    financialAdvisory: {
      title: "ADVISORY CHALLENGES. STRATEGIC SOLUTIONS",
      subtitle:
        "Addressing today's financial pressures with clarity-driven and future-ready guidance.",
      top: [
        {
          icon: "/assets/icons/regulatory.png",
          title: "Unclear Financial Direction",
          text: "Many businesses struggle to set a clear financial roadmap due to inconsistent cash flows & limited planning expertise.",
        },
        {
          icon: "/assets/icons/market.png",
          title: "High Risk Exposure",
          text: "Economic uncertainty and compliance changes make it difficult for companies to anticipate risks and protect long-term value.",
        },
        {
          icon: "/assets/icons/digital.png",
          title: "Inefficient Capital Allocation",
          text: "Teams often invest reactively rather than strategically, leading to underperforming assets and poor returns.",
        },
      ],
      bottom: [
        {
          icon: "/assets/icons/reg-check.png",
          text: "Strategic Clarity  We build structured financial plans, forecasting models, and actionable priorities for a clear direction.",
        },
        {
          icon: "/assets/icons/portfolio-check.png",
          text: "Risk Visibility  Our advisory approach identifies financial, operational & market risks early and creates safeguards.",
        },
        {
          icon: "/assets/icons/tech-check.png",
          text: "Smarter Investment  We help businesses evaluate opportunities, optimize capital allocation, and ensure every investment contributes to sustainable growth.",
        },
      ],
    },
    taxServices: {
      title: "TAX CHALLENGES & SOLUTIONS",
      subtitle:
        "Addressing tax complexity with solutions built for financial stability and real-world growth.",
      top: [
        {
          icon: "/assets/icons/regulatory.png",
          title: "Regulatory Complexity",
          text: "Constant rule changes make compliance harder, especially across multiple jurisdictions.",
        },
        {
          icon: "/assets/icons/market.png",
          title: "Rising Tax Risks",
          text: "Businesses face higher scrutiny around audits, documentation, and transfer pricing.",
        },
        {
          icon: "/assets/icons/digital.png",
          title: "Inefficient Tax Structures",
          text: "Traditional models limit cash flow, increase liabilities, and reduce investment potential.",
        },
      ],
      bottom: [
        {
          icon: "/assets/icons/reg-check.png",
          text: "Compliance Clarity  We simplify regulatory expectations, monitor updates, and build reporting frameworks that eliminate uncertainty.",
        },
        {
          icon: "/assets/icons/portfolio-check.png",
          text: "Early Risk Mitigation  We identify gaps early, strengthen audit readiness, and design processes that protect your financial standing.",
        },
        {
          icon: "/assets/icons/tech-check.png",
          text: "Strategic Tax Planning  We optimize structures, improve deductions, and build tax-efficient plans that support growth and profitability.",
        },
      ],
    },
    fintech: {
      title: "FINTECHS PRESSURES & PRACTICAL FIXES",
      subtitle:
        "Addressing real operational, financial, and regulatory hurdles digital finance companies face.",
      top: [
        {
          icon: "/assets/icons/regulatory.png",
          title: "Regulatory Burden",
          text: "Growing scrutiny across KYC, AML, data privacy, and cross-border compliance slows product rollout.",
        },
        {
          icon: "/assets/icons/market.png",
          title: "High Operating Costs",
          text: "Rising acquisition costs, fraud losses, and fragmented tech stacks reduce profitability and weaken cash flows.",
        },
        {
          icon: "/assets/icons/digital.png",
          title: "Customer Trust Gaps",
          text: "Security incidents, failed transactions, and limited transparency create trust concerns that impact adoption and retention.",
        },
      ],
      bottom: [
        {
          icon: "/assets/icons/reg-check.png",
          text: "Compliance Clarity  We design compliance roadmaps and enhance policy governance so FinTechs  scale without regulatory friction.",
        },
        {
          icon: "/assets/icons/portfolio-check.png",
          text: "Cost Optimization  We improve unit economics, streamline processes, and guide investments in scalable systems.",
        },
        {
          icon: "/assets/icons/tech-check.png",
          text: "Trust Frameworks  We enhance security protocols and customer communication models to build trusted digital finance experiences.",
        },
      ],
    },
    insurance: {
      title: "INSURANCE BARRIERS WITH SOLUTIONS",
      subtitle:
        "Addressing real-world pressures with strategies built for sustained insurance performance.",
      top: [
        {
          icon: "/assets/icons/regulatory.png",
          title: "Rising Claims Complexity",
          text: "More diverse risks, higher claim volumes, and inconsistent data make claims handling slow and costly.",
        },
        {
          icon: "/assets/icons/market.png",
          title: "Margin Pressure",
          text: "Low interest rates, volatile markets, and rising acquisition costs weaken profitability.",
        },
        {
          icon: "/assets/icons/digital.png",
          title: "Regulatory Load",
          text: "Insurers face strict solvency standards, new reporting formats, and increased audit expectations.",
        },
      ],
      bottom: [
        {
          icon: "/assets/icons/reg-check.png",
          text: "Claims Modernization  We simplify assessment workflows, integrate digital tools, and improve claims accuracy through data-driven models.",
        },
        {
          icon: "/assets/icons/portfolio-check.png",
          text: "Cost & Capital Efficiency  We strengthen investment strategies, optimize cost structures, and build capital plans that protect margins.",
        },
        {
          icon: "/assets/icons/tech-check.png",
          text: "Compliance Structuring  We design clear, manageable compliance frameworks that reduce complexity while safeguarding regulatory integrity.",
        },
      ],
    },
  };

  // =================== FRENCH DATA ===================
  const dataFR = {
    default: {
      title: "DÉFIS ET SOLUTIONS",
      subtitle: "Répondre aux points sensibles critiques de l'industrie",
      top: [
        {
          icon: "/assets/icons/regulatory.png",
          title: "Complexité Réglementaire",
          text: "Exigences de conformité évolutives dans plusieurs juridictions",
        },
        {
          icon: "/assets/icons/market.png",
          title: "Volatilité du Marché",
          text: "Conditions de marché imprévisibles affectant les stratégies d'investissement",
        },
        {
          icon: "/assets/icons/digital.png",
          title: "Transformation Numérique",
          text: "Systèmes hérités entravant l'efficacité opérationnelle",
        },
      ],
      bottom: [
        {
          icon: "/assets/icons/reg-check.png",
          text: "Surveillance automatisée de la conformité et perspectives d'experts",
        },
        {
          icon: "/assets/icons/portfolio-check.png",
          text: "Analyse des risques alimentée par l'IA et stratégies adaptatives",
        },
        {
          icon: "/assets/icons/tech-check.png",
          text: "Mise en œuvre de technologies modernes et optimisation des processus",
        },
      ],
    },
    assetManagement: {
      title: "RISQUES ET SOLUTIONS EN GESTION D'ACTIFS",
      subtitle:
        "Répondre aux pressions réelles de la gestion d'actifs avec des solutions pratiques et axées sur la performance",
      top: [
        {
          icon: "/assets/icons/regulatory.png",
          title: "Fardeau Réglementaire",
          text: "Les gestionnaires d'actifs font face à une pression constante pour respecter les réglementations mondiales en évolution.",
        },
        {
          icon: "/assets/icons/market.png",
          title: "Volatilité du Marché",
          text: "Les taux d'intérêt imprévisibles et les changements géopolitiques rendent la performance du portefeuille incohérente.",
        },
        {
          icon: "/assets/icons/digital.png",
          title: "Compression des Frais",
          text: "La concurrence croissante et la demande des investisseurs pour des frais plus bas réduisent les marges et limitent la flexibilité opérationnelle.",
        },
      ],
      bottom: [
        {
          icon: "/assets/icons/reg-check.png",
          text: "Intelligence de Conformité : Nous rationalisons les flux de travail de conformité et construisons des structures de reporting prêtes pour l'audit qui réduisent les erreurs manuelles.",
        },
        {
          icon: "/assets/icons/portfolio-check.png",
          text: "Portefeuilles Stabilisés : Nous concevons des modèles d'allocation résilients et appliquons une modélisation des risques basée sur des scénarios pour maintenir la performance stable.",
        },
        {
          icon: "/assets/icons/tech-check.png",
          text: "Optimisation de la Valeur : Nous améliorons les structures de coûts et identifions de nouvelles voies de revenus qui protègent les marges.",
        },
      ],
    },
    banking: {
      title: "PRESSIONS BANCAIRES, RÉPONSES PRATIQUES",
      subtitle:
        "Résoudre les obstacles financiers, opérationnels et réglementaires qui façonnent le secteur bancaire moderne.",
      top: [
        {
          icon: "/assets/icons/regulatory.png",
          title: "Surcharge de Conformité",
          text: "Les changements fréquents de politiques font de la conformité l'une des fonctions les plus gourmandes en ressources.",
        },
        {
          icon: "/assets/icons/market.png",
          title: "Croissance Ralentie",
          text: "Les systèmes obsolètes augmentent les coûts opérationnels et exposent à des lacunes en cybersécurité.",
        },
        {
          icon: "/assets/icons/digital.png",
          title: "Confiance Client en Déclin",
          text: "Avec la concurrence des fintechs et des banques numériques, les banques traditionnelles peinent à maintenir la confiance des clients.",
        },
      ],
      bottom: [
        {
          icon: "/assets/icons/reg-check.png",
          text: "Gouvernance Plus Intelligente : La surveillance automatisée et les contrôles des risques centralisés réduisent le travail manuel et diminuent les coûts de conformité.",
        },
        {
          icon: "/assets/icons/portfolio-check.png",
          text: "Modernisation Numérique : La mise à niveau vers des plateformes sécurisées et activées par le cloud améliore la vitesse et renforce la résilience.",
        },
        {
          icon: "/assets/icons/tech-check.png",
          text: "Réinvention de l'Expérience : La refonte du parcours basée sur les données restaure la confiance des clients et la fidélité à long terme.",
        },
      ],
    },
    financialAdvisory: {
      title: "DÉFIS CONSULTATIFS. SOLUTIONS STRATÉGIQUES",
      subtitle:
        "Répondre aux pressions financières d'aujourd'hui avec des conseils axés sur la clarté et prêts pour l'avenir.",
      top: [
        {
          icon: "/assets/icons/regulatory.png",
          title: "Direction Financière Floue",
          text: "De nombreuses entreprises peinent à établir une feuille de route financière claire en raison de flux de trésorerie incohérents et d'une expertise limitée en planification.",
        },
        {
          icon: "/assets/icons/market.png",
          title: "Exposition aux Risques Élevée",
          text: "L'incertitude économique et les changements de conformité rendent difficile pour les entreprises d'anticiper les risques et de protéger la valeur à long terme.",
        },
        {
          icon: "/assets/icons/digital.png",
          title: "Allocation de Capital Inefficace",
          text: "Les équipes investissent souvent de manière réactive plutôt que stratégique, entraînant des actifs sous-performants et de faibles rendements.",
        },
      ],
      bottom: [
        {
          icon: "/assets/icons/reg-check.png",
          text: "Clarté Stratégique : Nous construisons des plans financiers structurés, des modèles de prévision et des priorités exploitables pour une direction claire.",
        },
        {
          icon: "/assets/icons/portfolio-check.png",
          text: "Visibilité des Risques : Notre approche consultative identifie tôt les risques financiers, opérationnels et de marché et crée des protections.",
        },
        {
          icon: "/assets/icons/tech-check.png",
          text: "Investissement Plus Intelligent : Nous aidons les entreprises à évaluer les opportunités, optimiser l'allocation de capital et garantir que chaque investissement contribue à une croissance durable.",
        },
      ],
    },
    taxServices: {
      title: "DÉFIS ET SOLUTIONS FISCALES",
      subtitle:
        "Répondre à la complexité fiscale avec des solutions conçues pour la stabilité financière et la croissance réelle.",
      top: [
        {
          icon: "/assets/icons/regulatory.png",
          title: "Complexité Réglementaire",
          text: "Les changements constants de règles rendent la conformité plus difficile, en particulier dans plusieurs juridictions.",
        },
        {
          icon: "/assets/icons/market.png",
          title: "Risques Fiscaux Croissants",
          text: "Les entreprises font face à un examen plus approfondi concernant les audits, la documentation et les prix de transfert.",
        },
        {
          icon: "/assets/icons/digital.png",
          title: "Structures Fiscales Inefficaces",
          text: "Les modèles traditionnels limitent les flux de trésorerie, augmentent les passifs et réduisent le potentiel d'investissement.",
        },
      ],
      bottom: [
        {
          icon: "/assets/icons/reg-check.png",
          text: "Clarté de Conformité : Nous simplifions les attentes réglementaires, surveillons les mises à jour et construisons des cadres de reporting qui éliminent l'incertitude.",
        },
        {
          icon: "/assets/icons/portfolio-check.png",
          text: "Atténuation Précoce des Risques : Nous identifions les lacunes tôt, renforçons la préparation à l'audit et concevons des processus qui protègent votre position financière.",
        },
        {
          icon: "/assets/icons/tech-check.png",
          text: "Planification Fiscale Stratégique : Nous optimisons les structures, améliorons les déductions et construisons des plans fiscalement efficaces qui soutiennent la croissance et la rentabilité.",
        },
      ],
    },
    fintech: {
      title: "PRESSIONS FINTECH ET SOLUTIONS PRATIQUES",
      subtitle:
        "Répondre aux véritables obstacles opérationnels, financiers et réglementaires auxquels sont confrontées les entreprises de finance numérique.",
      top: [
        {
          icon: "/assets/icons/regulatory.png",
          title: "Fardeau Réglementaire",
          text: "L'examen croissant concernant le KYC, la LBC, la confidentialité des données et la conformité transfrontalière ralentit le déploiement des produits.",
        },
        {
          icon: "/assets/icons/market.png",
          title: "Coûts d'Exploitation Élevés",
          text: "L'augmentation des coûts d'acquisition, les pertes dues à la fraude et les piles technologiques fragmentées réduisent la rentabilité et affaiblissent les flux de trésorerie.",
        },
        {
          icon: "/assets/icons/digital.png",
          title: "Lacunes de Confiance Client",
          text: "Les incidents de sécurité, les transactions échouées et la transparence limitée créent des préoccupations de confiance qui impactent l'adoption et la rétention.",
        },
      ],
      bottom: [
        {
          icon: "/assets/icons/reg-check.png",
          text: "Clarté de Conformité : Nous concevons des feuilles de route de conformité et améliorons la gouvernance des politiques pour que les FinTechs évoluent sans friction réglementaire.",
        },
        {
          icon: "/assets/icons/portfolio-check.png",
          text: "Optimisation des Coûts : Nous améliorons l'économie unitaire, rationalisons les processus et guidons les investissements dans des systèmes évolutifs.",
        },
        {
          icon: "/assets/icons/tech-check.png",
          text: "Cadres de Confiance : Nous améliorons les protocoles de sécurité et les modèles de communication client pour construire des expériences de finance numérique de confiance.",
        },
      ],
    },
    insurance: {
      title: "OBSTACLES ASSURANTIELS AVEC SOLUTIONS",
      subtitle:
        "Répondre aux pressions du monde réel avec des stratégies conçues pour une performance assurancielle durable.",
      top: [
        {
          icon: "/assets/icons/regulatory.png",
          title: "Complexité Croissante des Sinistres",
          text: "Des risques plus diversifiés, des volumes de sinistres plus élevés et des données incohérentes rendent le traitement des sinistres lent et coûteux.",
        },
        {
          icon: "/assets/icons/market.png",
          title: "Pression sur les Marges",
          text: "Les taux d'intérêt bas, les marchés volatils et les coûts d'acquisition croissants affaiblissent la rentabilité.",
        },
        {
          icon: "/assets/icons/digital.png",
          title: "Charge Réglementaire",
          text: "Les assureurs font face à des normes de solvabilité strictes, de nouveaux formats de reporting et des attentes d'audit accrues.",
        },
      ],
      bottom: [
        {
          icon: "/assets/icons/reg-check.png",
          text: "Modernisation des Sinistres : Nous simplifions les flux de travail d'évaluation, intégrons des outils numériques et améliorons la précision des sinistres grâce à des modèles basés sur les données.",
        },
        {
          icon: "/assets/icons/portfolio-check.png",
          text: "Efficacité des Coûts et du Capital : Nous renforçons les stratégies d'investissement, optimisons les structures de coûts et construisons des plans de capital qui protègent les marges.",
        },
        {
          icon: "/assets/icons/tech-check.png",
          text: "Structuration de la Conformité : Nous concevons des cadres de conformité clairs et gérables qui réduisent la complexité tout en préservant l'intégrité réglementaire.",
        },
      ],
    },
  };

  // =================== DATA SELECTION BASED ON PATH ===================
  const path = location.pathname;
  const languageData = isFrench ? dataFR : dataEN;

  const data =
    path === "/industries/financialservices/assetmanagement"
      ? languageData.assetManagement
      : path === "/industries/financialservices/banking"
      ? languageData.banking
      : path === "/industries/financialservices/financialadvisoryservices"
      ? languageData.financialAdvisory
      : path === "/industries/financialservices/financialservicetax"
      ? languageData.taxServices
      : path === "/industries/financialservices/fintech"
      ? languageData.fintech
      : path === "/industries/financialservices/insurance"
      ? languageData.insurance
      : languageData.default;

  return (
    <div className="challenges-main-div">
      {/* ================= HEADING ================= */}
      <div className="text-center mb-5">
        <h2 className="challenges-title">{data.title}</h2>
        <h6 className="challenges-subtitle">{data.subtitle}</h6>
      </div>

      {/* ================= TOP CHALLENGES ================= */}
      <div className="row justify-content-center challenges-top-row">
        {data.top.map((item, index) => (
          <div className="col-md-4 text-center" key={index}>
            <div className="challenges-top-box">
              <div className="challenges-icon">
                <img src={item.icon} alt="" />
              </div>

              <h5 className="challenges-top-title">{item.title}</h5>
              <p className="challenges-top-text">{item.text}</p>

              {/* orange line */}
              <div className="challenges-line"></div>
            </div>
          </div>
        ))}
      </div>

      {/* ================= BOTTOM SOLUTIONS ================= */}
      <div className="row justify-content-center mt-3 challenges-bottom-row">
        {data.bottom.map((item, index) => (
          <div className="col-md-4 text-center" key={index}>
            <div className="challenges-bottom-box">
              <div className="challenges-solution-icon">
                <img src={item.icon} alt="" />
              </div>

              <p className="challenges-bottom-text">{item.text}</p>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}